import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Trash2, Sword, Shield, Heart,
  Search, Filter, SortDesc, Award, Dices,
  AlertCircle, Plus, Star, Eye
} from 'lucide-react';
import { useCharacter } from '../contexts/CharacterContext';
import { gameRuleService } from '../services/gameRuleService';
import CharacterViewer from './CharacterViewer';
import './CharacterList.css';

const CharacterList = () => {
  const { characters, deleteCharacter, fetchCharacters, loading } = useCharacter();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState({ class: '', race: '', level: '' });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [viewingCharacter, setViewingCharacter] = useState(null);

  // Fetch characters on mount
  useEffect(() => {
    fetchCharacters();
  }, [fetchCharacters]);

  // Filter and sort characters
  const filteredCharacters = React.useMemo(() => {
    let result = [...characters];

    // Apply filters
    result = result.filter(char => {
      const matchesSearch = char.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesClass = !filterBy.class || char.characterClass === filterBy.class;
      const matchesRace = !filterBy.race || char.race === filterBy.race;
      const matchesLevel = !filterBy.level || char.level.toString() === filterBy.level;
      return matchesSearch && matchesClass && matchesRace && matchesLevel;
    });

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'level':
          comparison = a.level - b.level;
          break;
        case 'class':
          comparison = a.characterClass.localeCompare(b.characterClass);
          break;
        case 'race':
          comparison = a.race.localeCompare(b.race);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [characters, searchTerm, filterBy, sortBy, sortOrder]);

  const CharacterStats = ({ character }) => {
    const stats = [
      { icon: <Heart size={16} />, value: character.maxHealth, label: 'HP' },
      { icon: <Sword size={16} />, value: character.strength, label: 'STR' },
      { icon: <Shield size={16} />, value: character.armorClass, label: 'AC' },
      { icon: <Dices size={16} />, value: character.dexterity, label: 'DEX' },
      { icon: <Award size={16} />, value: character.level, label: 'LVL' }
    ];

    return (
      <div className="character-card__stats">
        {stats.map((stat, index) => (
          <div key={index} className="character-card__stat">
            {stat.icon}
            <span className="character-card__stat-value">{stat.value}</span>
            <span className="character-card__stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const CharacterCard = ({ character }) => {
    const isConfirmingDelete = deleteConfirmId === character.id;

    return (
      <motion.div
        className="character-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        <div className="character-card__header">
          <div className="character-card__avatar">
            <User size={24} />
          </div>
          <div className="character-card__info">
            <h3>{character.name}</h3>
            <p>Level {character.level} {character.race} {character.characterClass}</p>
          </div>
        </div>

        <CharacterStats character={character} />

        <div className="character-card__actions">
          {isConfirmingDelete ? (
            <motion.div className="character-card__confirm">
              <button
                className="character-card__confirm-yes"
                onClick={() => deleteCharacter(character.id)}
              >
                Confirm Delete
              </button>
              <button
                className="character-card__confirm-no"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </button>
            </motion.div>
          ) : (
            <>
              <button
                className="character-card__view"
                onClick={() => setViewingCharacter(character)}
              >
                <Eye size={16} />
                View
              </button>
              <button
                className="character-card__delete"
                onClick={() => setDeleteConfirmId(character.id)}
              >
                <Trash2 size={16} />
                Delete
              </button>
            </>
          )}
        </div>
      </motion.div>
    );
  };

  const FilterPanel = () => (
    <motion.div
      className="character-list__filters"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
    >
      <div className="character-list__filter">
        <label>Class</label>
        <select
          value={filterBy.class}
          onChange={(e) => setFilterBy({ ...filterBy, class: e.target.value })}
        >
          <option value="">All Classes</option>
          {gameRuleService.getClasses().map(c => (
            <option key={c.name} value={c.name}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="character-list__filter">
        <label>Race</label>
        <select
          value={filterBy.race}
          onChange={(e) => setFilterBy({ ...filterBy, race: e.target.value })}
        >
          <option value="">All Races</option>
          {gameRuleService.getRaces().map(r => (
            <option key={r.name} value={r.name}>{r.name}</option>
          ))}
        </select>
      </div>

      <div className="character-list__filter">
        <label>Level</label>
        <select
          value={filterBy.level}
          onChange={(e) => setFilterBy({ ...filterBy, level: e.target.value })}
        >
          <option value="">All Levels</option>
          {[...Array(20)].map((_, i) => (
            <option key={i + 1} value={i + 1}>Level {i + 1}</option>
          ))}
        </select>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="character-list-container">
        <div className="character-list__loading">
          <div className="character-list__loading-spinner" />
          <p>Summoning your characters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="character-list-container">
      <header className="character-list__header">
        <h1 className="character-list__title">Your Characters</h1>
        
        <div className="character-list__controls">
          <div className="character-list__search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search characters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            className="character-list__button"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          <button
            className="character-list__button"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            <SortDesc size={16} className={sortOrder === 'desc' ? 'rotated' : ''} />
            Sort {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {showFilters && <FilterPanel />}
      </AnimatePresence>

      {filteredCharacters.length === 0 ? (
        <motion.div
          className="character-list__empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {searchTerm || Object.values(filterBy).some(Boolean) ? (
            <>
              <AlertCircle size={48} />
              <p>No characters match your search criteria.</p>
              <button
                className="character-list__button"
                onClick={() => {
                  setSearchTerm('');
                  setFilterBy({ class: '', race: '', level: '' });
                }}
              >
                Clear Filters
              </button>
            </>
          ) : (
            <>
              <User size={48} />
              <p>You haven't created any characters yet.</p>
              <Link to="/create-character" className="character-list__create-button">
                <Plus size={20} />
                Create Your First Character
              </Link>
            </>
          )}
        </motion.div>
      ) : (
        <>
          <div className="character-list__grid">
            <AnimatePresence>
              {filteredCharacters.map(character => (
                <CharacterCard key={character.id} character={character} />
              ))}
            </AnimatePresence>
          </div>

          <motion.div
            className="character-list__footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Link to="/create-character" className="character-list__create-button">
              <Star size={20} />
              Create New Character
            </Link>
          </motion.div>
        </>
      )}

      {viewingCharacter && (
        <div className="modal-overlay" onClick={() => setViewingCharacter(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <CharacterViewer 
              character={viewingCharacter} 
              onClose={() => setViewingCharacter(null)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterList;